import React, { useEffect, useId, useRef } from 'react';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  children: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

// Built on the native <dialog> so focus trapping, Escape to close and the
// backdrop come from the browser. The body is a form, so Enter in an input
// confirms. showModal() focuses the first focusable element: the first input
// when there is one, otherwise Cancel.
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pressStartedOnBackdrop = useRef(false);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Escape fires `cancel`; keep React state in charge of closing.
  const handleCancelEvent = (event: React.SyntheticEvent) => {
    event.preventDefault();
    if (!busy) onCancel();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!busy && !confirmDisabled) onConfirm();
  };

  // A press whose target is the dialog itself landed on the backdrop. Both
  // ends must be there, so a text selection dragged out of an input doesn't
  // close the dialog.
  const handleMouseDown = (event: React.MouseEvent<HTMLDialogElement>) => {
    pressStartedOnBackdrop.current = event.target === event.currentTarget;
  };

  const handleClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    const onBackdrop =
      pressStartedOnBackdrop.current && event.target === event.currentTarget;
    pressStartedOnBackdrop.current = false;
    if (onBackdrop && !busy) onCancel();
  };

  return (
    <dialog
      ref={dialogRef}
      className="confirm-dialog"
      aria-labelledby={titleId}
      onCancel={handleCancelEvent}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <form className="confirm-dialog-body" onSubmit={handleSubmit} noValidate>
        <h2 id={titleId} className="confirm-dialog-title">
          {title}
        </h2>
        <div className="confirm-dialog-content">{children}</div>
        <div className="confirm-dialog-actions">
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type="submit"
            className={`btn ${destructive ? 'btn-danger' : 'btn-primary'}`}
            disabled={busy || confirmDisabled}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
};

export default ConfirmDialog;
